import { useImperativeHandle } from "react";
import { addOrcaEvent } from "../../utils";
import { AdState, OrcaWindowEvents } from "../../../globals";
import { useVideoDataContext } from "../../contexts/VideoDataContext";
import { useAdsElementsRefContext } from "../../contexts/AdsElementsRefContext";
import { useVideoElementRefContext } from "../../contexts/VideoElementRefContext";
import { useAutoplayContext } from "../../contexts/AutoplayContext";
import { useOrcaCommonContext } from "../../contexts/OrcaCommonContext";
import { useOrcaAdsContext } from "../../contexts/OrcaAdsContext";
/**
 * Surfaces the methods that are available on orca refs.
 */
export const useVideoHandlers = (
  ref,
  hls,
  setRenderControls,
  promoEnabled,
  initializeNewVideo,
  setUpVideoSource,
  transitionToVideo
) => {
  const { videoId, uniqueId, resetUniqueId = () => {} } = useVideoDataContext();
  const { adControlsRef } = useAdsElementsRefContext();
  const videoElementRef = useVideoElementRefContext();
  const { setOverrideReducedMotion } = useAutoplayContext();
  const { adState } = useOrcaAdsContext();
  const {
    promoElement,
    playerState,
    resetVideoState,
    setShowSubtitles,
    toggleMute,
    reset,
    pause,
    play
  } = useOrcaCommonContext();

  useImperativeHandle(
    ref,
    () => ({
      resetVideo() {
        reset();
        addOrcaEvent(OrcaWindowEvents.RESET_CAROUSEL_VIDEO, true, videoId);
      },

      start() {
        setUpVideoSource();
        transitionToVideo();
      },

      startVideoLoad() {
        setUpVideoSource();
      },

      stopVideoLoad() {
        hls?.stopLoad();
      },

      showControls() {
        setRenderControls(true);
      },

      playVideoPreview() {
        if (promoEnabled) promoElement.current.playVideo();
        play();
      },

      play() {
        play();
      },

      pause() {
        pause();
      },

      toggleMute() {
        if (adState === AdState.PLAYING) {
          adControlsRef.current.toggleAdMute();
        } else {
          toggleMute();
        }
      },

      setTime(time) {
        if (adState === AdState.PLAYING) return;
        if (typeof time === "number")
          videoElementRef.current.currentTime = time;
      },

      restart() {
        if (adState === AdState.PLAYING) return;
        videoElementRef.current.currentTime = 0;
      },

      initializeNewVideo(videoWillAutoplay, overrideReducedMotion) {
        initializeNewVideo(videoWillAutoplay, overrideReducedMotion);
      },

      resetVideoState(hasNextVideo) {
        resetVideoState(hasNextVideo);
      },

      getDomNode() {
        return videoElementRef.current;
      },

      toggleSubtitles() {
        setShowSubtitles(!playerState.showSubtitles);
      },

      setReducedMotion(reduceMotion) {
        setOverrideReducedMotion(reduceMotion);
      },

      getUniqueId() {
        return uniqueId;
      },

      on(event, callback) {
        videoElementRef.current.addEventListener(event, callback);
      },

      off(event, callback) {
        videoElementRef.current.removeEventListener(event, callback);
      },

      resetUniqueId() {
        resetUniqueId();
      },

      videoPlayerState: playerState
    }),
    [
      playerState,
      reset,
      videoId,
      setUpVideoSource,
      transitionToVideo,
      initializeNewVideo,
      hls,
      setRenderControls,
      promoEnabled,
      promoElement,
      play,
      pause,
      adState,
      adControlsRef,
      toggleMute,
      videoElementRef,
      resetVideoState,
      setShowSubtitles,
      setOverrideReducedMotion,
      uniqueId,
      resetUniqueId
    ]
  );
};
